import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type { ColorScheme }

const tokens = {
  dark: {
    bg: 'bg.card',
    border: 'border.subtle',
    title: 'text.primary',
    secondary: 'text.secondary',
  },
  light: {
    bg: 'light.bg.card',
    border: 'light.border.subtle',
    title: 'light.text.primary',
    secondary: 'light.text.secondary',
  },
} as const

/** The inlaid panel holding the header, tiles and selected-tile content. */
export const summaryPanelStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  bg: tokens[colorScheme].bg,
  border: '1px solid',
  borderColor: tokens[colorScheme].border,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  minW: 0,
  p: '24px',
  w: 'full',
})

export const summaryPanelHeaderStyles: SystemStyleObject = {
  alignItems: 'flex-start',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '24px',
  justifyContent: 'space-between',
}

export const summaryPanelTitleGroupStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minW: 0,
}

export const summaryPanelTitleStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: tokens[colorScheme].title,
  m: 0,
  textStyle: 'heading-l',
})

export const summaryPanelDescriptionStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: tokens[colorScheme].secondary,
  textStyle: 'caption',
})

// Wraps rather than overflowing: with `headerActions` beside the secondary
// action the row can be wider than a narrow panel, even once the header has
// moved it below the title.
export const summaryPanelActionsStyles: SystemStyleObject = {
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  flexWrap: 'wrap',
  gap: '8px',
  maxW: '100%',
  minW: 0,
}

/**
 * Wrapper around the secondary action's Button. While the action is disabled
 * it takes WorkflowButton's disabled treatment — the enabled outline faded to
 * 35% opacity — instead of the secondary variant's ghost tokens, so it reads
 * like the incident drawer's disabled Workflows button. The secondary Button
 * variant itself is unchanged.
 */
export const summaryPanelActionButtonStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  // Both disabling mechanisms: the plain attribute, and aria-disabled (used
  // when a disabledReason keeps the button focusable).
  "& button:is(:disabled, [aria-disabled='true']), & button:is(:disabled, [aria-disabled='true']):hover":
    {
      bg: 'transparent',
      borderColor:
        colorScheme === 'light'
          ? 'light.interactive.tertiary-border'
          : 'interactive.tertiary-border',
      color: colorScheme === 'light' ? 'light.text.primary' : 'text.primary',
      opacity: 0.35,
    },
})

export const summaryPanelLoadingStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  alignItems: 'center',
  color: tokens[colorScheme].secondary,
  display: 'flex',
  gap: '8px',
  justifyContent: 'center',
  py: '24px',
  textStyle: 'caption',
})

/** Bordered box under the tiles showing the selected tile's content. */
export const summaryPanelContentStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  border: '1px solid',
  borderColor: tokens[colorScheme].border,
  display: 'flex',
  flexDirection: 'column',
  minW: 0,
})

export const summaryPanelContentHeaderStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  borderBottom: '1px solid',
  borderBottomColor: tokens[colorScheme].border,
  color: tokens[colorScheme].secondary,
  px: '16px',
  py: '12px',
  textStyle: 'overline',
})

export const summaryPanelContentBodyStyles = (
  maxHeight?: string,
  isFlush = false
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  minW: 0,
  ...(!isFlush && { pb: '8px', pt: '4px', px: '16px' }),
  ...(maxHeight && {
    maxH: maxHeight,
    overflowY: 'auto',
    scrollbarGutter: 'stable',
  }),
})
